"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAISync } from "@stockline/web";
import type { AgentExecutionLight } from "@stockline/shared/types/ai/execution";
import { v2Api } from "@/lib/v2-api";
import { whatsappKeys } from "@/query/keys/whatsapp";
import { smsKeys } from "@/query/hooks/useSMS";
import { emailKeys } from "@/query/keys/emails";
import type { Query } from "@tanstack/react-query";

const EXECUTION_TABLE = "executions";

const isChatListQuery = (query: Query) =>
  Array.isArray(query.queryKey) && query.queryKey[2] === "list";

export function useTriggerWorkflowBatch() {
  const queryClient = useQueryClient();
  const { storage } = useAISync();

  return useMutation({
    mutationFn: (body: {
      items: Array<{ channel: "email" | "sms" | "whatsapp"; id: string }>;
      workflowId: string;
      instructions?: string;
    }) => v2Api.executions.trigger(body),
    onSuccess: async (data, variables) => {
      const nowIso = new Date().toISOString();
      const optimisticRows: AgentExecutionLight[] = data.succeeded
        .filter((item) => !!item.execution_id)
        .map((item) => ({
          id: item.execution_id as string,
          agentType: data.workflow_id ?? variables.workflowId,
          agentName: data.workflow_name ?? null,
          status: (item.status as AgentExecutionLight["status"]) ?? "pending",
          errorMessage: null,
          startedAt: null,
          createdAt: nowIso,
          syncId: 0,
        }));

      if (optimisticRows.length > 0) {
        await storage.putMany<AgentExecutionLight>(
          EXECUTION_TABLE,
          optimisticRows,
        );
      }

      const channels = new Set(variables.items.map((i) => i.channel));

      if (channels.has("whatsapp")) {
        queryClient.invalidateQueries({
          queryKey: whatsappKeys.chats(),
          predicate: isChatListQuery,
        });
      }
      if (channels.has("sms")) {
        queryClient.invalidateQueries({ queryKey: smsKeys.chats() });
      }
      if (channels.has("email")) {
        queryClient.invalidateQueries({ queryKey: emailKeys.all });
      }
    },
  });
}
